import { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import Container from '@mui/material/Container';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import TextField from '@mui/material/TextField';
import Button from '@mui/material/Button';
import Paper from '@mui/material/Paper';
import Alert from '@mui/material/Alert';
import IconButton from '@mui/material/IconButton';
import InputAdornment from '@mui/material/InputAdornment';
import Visibility from '@mui/icons-material/Visibility';
import VisibilityOff from '@mui/icons-material/VisibilityOff';
import MuiLink from '@mui/material/Link';
import Divider from '@mui/material/Divider';
import Grid from '@mui/material/Grid';
import Avatar from '@mui/material/Avatar';
import LocalHospitalIcon from '@mui/icons-material/LocalHospital';
import MedicalServicesIcon from '@mui/icons-material/MedicalServices';
import { supabaseAuthService } from '../services/supabaseAuthService';
import { keyframes } from '@mui/system';

const fadeIn = keyframes`
  from {
    opacity: 0;
    transform: translateY(20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

const PhysicianLogin = () => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({ email: '', password: '' });
  const [errors, setErrors] = useState({});
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const validateForm = () => {
    let valid = true;
    const newErrors = {};
    if (!formData.email) {
      newErrors.email = 'Email is required';
      valid = false;
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = 'Please enter a valid email address';
      valid = false;
    }
    if (!formData.password) {
      newErrors.password = 'Password is required';
      valid = false;
    } else if (formData.password.length < 6) {
      newErrors.password = 'Password must be at least 6 characters';
      valid = false;
    }
    setErrors(newErrors);
    return valid;
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
    if (errors[name]) {
      setErrors({ ...errors, [name]: '' });
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (validateForm()) {
      setIsLoading(true);
      try {
        const { data, error } = await supabaseAuthService.signIn(
          formData.email,
          formData.password
        );
        
        if (error) {
          throw error;
        }
        
        if (data?.user) {
          // Successfully logged in - wait for AuthContext to update
          // Then navigate (Dashboard will redirect physicians to clinic dashboard)
          setTimeout(() => {
            navigate('/dashboard');
          }, 500);
        }
      } catch (error) {
        console.error('Error logging in:', error);
        let errorMessage = 'Invalid email or password';
        
        if (error.message?.includes('Invalid login credentials')) {
          errorMessage = 'Invalid email or password. Please try again.';
        } else if (error.message?.includes('Email not confirmed')) {
          errorMessage = 'Please check your email and confirm your account.';
        } else if (error.message?.includes('Too many requests')) {
          errorMessage = 'Too many failed attempts. Please try again later.';
        } else if (error.message) {
          errorMessage = error.message;
        }
        
        setErrors({ ...errors, submit: errorMessage });
      } finally {
        setIsLoading(false);
      }
    }
  };

  const handleTogglePasswordVisibility = () => {
    setShowPassword(!showPassword);
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        bgcolor: 'background.default',
        py: 4,
      }}
    >
      <Container maxWidth="lg" sx={{ px: { xs: 1, sm: 2 }, width: '100%' }}>
        <Grid container spacing={4} alignItems="center">
          <Grid item xs={12} md={6}>
            <Box sx={{ maxWidth: 400, mx: 'auto', textAlign: 'center' }}>
              <Avatar 
                sx={{ 
                  bgcolor: 'success.main', 
                  width: 80, 
                  height: 80, 
                  mx: 'auto',
                  mb: 2,
                  boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)',
                }}
              >
                <LocalHospitalIcon sx={{ fontSize: 40 }} />
              </Avatar>
              <Typography
                variant="h3"
                component="h1"
                sx={{
                  fontWeight: 700,
                  color: 'success.main',
                  mb: 2,
                  letterSpacing: '-0.5px',
                  fontSize: { xs: '1.5rem', sm: '2rem' },
                }}
              >
                Physician Portal
              </Typography>
              <Typography
                variant="h6"
                color="text.secondary"
                sx={{ mb: 4, fontWeight: 400, fontSize: { xs: '0.875rem', sm: '1rem' } }}
              >
                Access your clinical dashboard to manage patients and appointments
              </Typography>
            </Box>
          </Grid>
          <Grid item xs={12} md={6}>
            <Paper 
              elevation={0} 
              sx={{ 
                p: { xs: 2, sm: 3 }, 
                width: '100%', 
                maxWidth: 400, 
                mx: 'auto', 
                overflowX: 'auto',
                borderRadius: 4,
                boxShadow: '0 4px 20px rgba(0, 0, 0, 0.05)',
                animation: `${fadeIn} 0.5s ease-out`,
                '&:hover': {
                  boxShadow: '0 8px 24px rgba(0, 0, 0, 0.1)',
                },
                transition: 'box-shadow 0.3s ease-in-out',
              }}
            >
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
            <Box sx={{ 
              display: 'flex', 
              alignItems: 'center', 
              justifyContent: 'center', 
              mb: 3,
              flexDirection: 'column'
            }}>
              <Avatar 
                sx={{ 
                  bgcolor: 'success.main', 
                  width: 56, 
                  height: 56, 
                  mb: 2,
                  boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)',
                }}
              >
                <MedicalServicesIcon fontSize="large" />
              </Avatar>
              <Typography 
                component="h2" 
                variant="h4" 
                sx={{ 
                  fontWeight: 700,
                  color: 'success.main',
                  textAlign: 'center',
                  letterSpacing: '-0.5px', 
                  fontSize: { xs: '1.25rem', sm: '1.5rem' } 
                }}
              >
                Physician Login
              </Typography>
            </Box>
            
            {errors.submit && (
              <Alert severity="error" sx={{ width: '100%', mb: 2, animation: `${fadeIn} 0.3s ease-out` }}>
                {errors.submit}
              </Alert>
            )}
            <Box component="form" sx={{ width: '100%' }} onSubmit={handleSubmit}>
              <TextField
                margin="normal"
                required
                fullWidth
                id="email"
                label="Email Address"
                name="email"
                autoComplete="email"
                autoFocus
                value={formData.email}
                onChange={handleChange}
                error={!!errors.email}
                helperText={errors.email}
                sx={{ 
                  '& .MuiOutlinedInput-root': { 
                    borderRadius: 2,
                    '& fieldset': {
                      borderColor: 'rgba(0, 0, 0, 0.1)',
                    },
                    '&:hover fieldset': { 
                      borderColor: 'success.main' 
                    } 
                  } 
                }}
              />
              <TextField
                margin="normal"
                required
                fullWidth
                name="password"
                label="Password"
                type={showPassword ? 'text' : 'password'}
                id="password"
                autoComplete="current-password"
                value={formData.password}
                onChange={handleChange}
                error={!!errors.password}
                helperText={errors.password}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton onClick={handleTogglePasswordVisibility} edge="end">
                        {showPassword ? <VisibilityOff /> : <Visibility />}
                      </IconButton>
                    </InputAdornment>
                  )
                }}
                sx={{ 
                  '& .MuiOutlinedInput-root': { 
                    borderRadius: 2,
                    '& fieldset': {
                      borderColor: 'rgba(0, 0, 0, 0.1)',
                    },
                    '&:hover fieldset': { 
                      borderColor: 'success.main' 
                    } 
                  } 
                }}
              />
              <Button
                type="submit"
                fullWidth
                variant="contained"
                color="success"
                sx={{ 
                  mt: 3, 
                  mb: 2, 
                  py: 1.5,
                  borderRadius: 2,
                  boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)',
                  transition: 'all 0.3s ease',
                  fontWeight: 600,
                  '&:hover': {
                    transform: 'translateY(-2px)',
                    boxShadow: '0 6px 16px rgba(0, 0, 0, 0.15)',
                  }
                }}
                disabled={isLoading}
              >
                {isLoading ? 'Signing In...' : 'Sign In'}
              </Button>
              
              <Box sx={{ textAlign: 'right', mb: 2 }}>
                <MuiLink 
                  component={Link} 
                  to="/forgot-password" 
                  variant="body2" 
                  color="success.main"
                  sx={{
                    textDecoration: 'none',
                    '&:hover': {
                      textDecoration: 'underline',
                    },
                  }}
                >
                  Forgot password?
                </MuiLink>
              </Box>
            </Box>
            
            <Divider sx={{ my: 3, width: '100%' }}>
              <Typography variant="body2" color="text.secondary">
                OR
              </Typography>
            </Divider>
            
            <Box sx={{ textAlign: 'center', width: '100%' }}>
              <MuiLink 
                component={Link} 
                to="/login" 
                variant="body1" 
                color="success.main"
                sx={{
                  textDecoration: 'none',
                  '&:hover': {
                    textDecoration: 'underline',
                  },
                }}
              >
                Not a physician? Patient Login
              </MuiLink>
            </Box>
          </Box>
        </Paper>
          </Grid>
        </Grid>
      </Container>
    </Box>
  );
};

export default PhysicianLogin;
